/**
 * UICommands - Phase A Mission A2.2
 *
 * Defines the "language" that Soma/Chat/Voice will use to control
 * the Studio v2 layout. For A2, these are just types and stubbed handlers.
 * Actual wiring to agents comes in Phase B.
 *
 * Command types:
 * - open_panel: Open a specific panel in its designated slot
 * - toggle_drawer: Open/close the drawer or switch drawer tabs
 * - set_center_app: Switch center-main to an app by its key
 */

import type { StudioPanelSlot } from './registry.js';
import {
    setStudioPanelState,
    setCenterMainByAppKey,
    toggleDrawer,
    getStudioPanelState,
} from './panelManager.js';
import { getPanelById, getPanelByAppKey } from './registry.js';

// ============================================
// COMMAND TYPES
// ============================================

/**
 * Open a panel in its designated slot (or override slot if provided)
 */
export interface OpenPanelCommand {
    type: 'open_panel';
    /** Panel ID to open (e.g., "missions-main", "drawer-logs") */
    panelId: string;
    /** Optional slot override - defaults to panel's registered slot */
    slot?: StudioPanelSlot;
}

/**
 * Toggle the drawer open/closed or switch to a specific drawer tab
 */
export interface ToggleDrawerCommand {
    type: 'toggle_drawer';
    /** Optional panel ID to switch to (e.g., "drawer-logs") */
    panelId?: string;
}

/**
 * Set the center-main panel by app key (e.g., "convops", "builder")
 */
export interface SetCenterAppCommand {
    type: 'set_center_app';
    /** App key from SidebarV2 (e.g., "convops", "builder", "matrix") */
    appKey: string;
}

/**
 * Union of all Studio UI commands
 */
export type StudioUICommand = OpenPanelCommand | ToggleDrawerCommand | SetCenterAppCommand;

// ============================================
// COMMAND HANDLER (stubbed for A2, wired in Phase B)
// ============================================

/**
 * Apply a Studio UI command to the panel state
 *
 * For A2, this is a simple switch that calls setStudioPanelState.
 * In Phase B, this will be called by Soma/Chat/Voice agents.
 */
export function applyStudioUICommand(cmd: StudioUICommand): void {
    console.log('[UICommands] Applying command:', cmd);

    switch (cmd.type) {
        case 'open_panel': {
            const panel = getPanelById(cmd.panelId);
            if (!panel) {
                console.warn(`[UICommands] Unknown panel ID: "${cmd.panelId}"`);
                return;
            }

            // Determine which slot to update based on panel's registered slot
            const slot = cmd.slot ?? panel.slot;
            switch (slot) {
                case 'center-main':
                    setStudioPanelState({ centerMainId: cmd.panelId });
                    break;
                case 'right-rail':
                    setStudioPanelState({ rightRailId: cmd.panelId });
                    break;
                case 'drawer':
                    setStudioPanelState({ drawerId: cmd.panelId });
                    break;
            }
            break;
        }

        case 'toggle_drawer': {
            toggleDrawer(cmd.panelId);
            break;
        }

        case 'set_center_app': {
            // First check if there's a panel with this app key
            const panel = getPanelByAppKey(cmd.appKey);
            if (panel) {
                setStudioPanelState({ centerMainId: panel.id });
            } else {
                // Fall back to convention: {appKey}-main
                setCenterMainByAppKey(cmd.appKey);
            }
            break;
        }

        default: {
            // TypeScript exhaustiveness check
            const _exhaustive: never = cmd;
            console.warn('[UICommands] Unknown command type:', _exhaustive);
        }
    }
}

/**
 * Parse a natural language intent into a UICommand (stub for Phase B)
 *
 * This will be implemented in Phase B when we wire Soma/Chat.
 * For now, it's just a placeholder showing the intended interface.
 */
export function parseUIIntent(_intent: string): StudioUICommand | null {
    // TODO: Phase B - wire to Soma/Chat for NL parsing
    console.log('[UICommands] parseUIIntent not yet implemented');
    return null;
}

/**
 * Get the current panel state (convenience re-export)
 */
export { getStudioPanelState } from './panelManager.js';
